import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import {
  ChatApiMessage,
  ChatConversation,
  ChatService,
  ChatStatus,
} from '../../../../services/chat';

@Component({
  selector: 'app-mychattickets',
  templateUrl: './mychattickets.component.html',
  styleUrl: './mychattickets.component.scss',
})
export class MychatticketsComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  @ViewChild('messagesScroller') private messagesScroller?: ElementRef<HTMLElement>;

  chats: ChatConversation[] = [];
  selectedChat: ChatConversation | null = null;
  selectedChatId: number | string | null = null;
  isDetailView = false;
  detailMessageDraft = '';

  isLoading = true;
  isSendingMessage = false;
  errorMessage = '';

  private routeSub?: Subscription;
  private shouldScrollToBottom = false;

  constructor(
    private chatService: ChatService,
    private route: ActivatedRoute,
    private router: Router,
    private transloco: TranslocoService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const chatId = params.get('id');

      if (chatId) {
        this.isDetailView = true;
        this.selectedChatId = chatId;
        this.loadChat(chatId);
        return;
      }

      this.isDetailView = false;
      this.selectedChatId = null;
      this.selectedChat = null;
      this.loadChats();
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollMessagesToBottom();
    }
  }

  loadChats(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.chatService.getMyChats(50).subscribe({
      next: (res) => {
        this.chats = Array.isArray(res?.data) ? res.data : [];
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage =
          err?.error?.message || this.t('atw.myChats.errors.loadChats');
      },
    });
  }

  loadChat(chatId: number | string): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.chatService.getConversation(chatId).subscribe({
      next: (res) => {
        this.selectedChat = res.conversation;
        this.isLoading = false;
        this.queueScrollToBottom();
      },
      error: (err) => {
        this.isLoading = false;
        this.errorMessage =
          err?.error?.message || this.t('atw.myChats.errors.loadChat');
      },
    });
  }

  openChat(chatId: number): void {
    this.router.navigate(['/Mychats', chatId]);
  }

  backToChats(): void {
    this.router.navigate(['/Mychats']);
  }

  retryLoad(): void {
    if (this.isDetailView && this.selectedChatId) {
      this.loadChat(this.selectedChatId);
      return;
    }

    this.loadChats();
  }

  sendMessage(): void {
    const message = this.detailMessageDraft.trim();

    if (!message || !this.selectedChat || this.isSendingMessage) {
      return;
    }

    this.isSendingMessage = true;
    this.errorMessage = '';

    this.chatService
      .sendMessage({
        conversation_id: this.selectedChat.id,
        session_id: this.selectedChat.session_id || undefined,
        user_type: this.selectedChat.user_type,
        message_type: 'text',
        message,
        source_page: 'Mychats',
      })
      .subscribe({
        next: (res) => {
          this.selectedChat = res.conversation;
          this.detailMessageDraft = '';
          this.isSendingMessage = false;
          this.queueScrollToBottom();
        },
        error: (err) => {
          this.isSendingMessage = false;
          this.errorMessage =
            err?.error?.message || this.t('atw.myChats.errors.send');
        },
      });
  }

  trackByChatId(_: number, chat: ChatConversation): number {
    return chat.id;
  }

  trackByMessageId(_: number, message: ChatApiMessage): number {
    return message.id;
  }

  getStatusLabel(status: ChatStatus): string {
    return this.t(`atw.myChats.status.${status}`) || status;
  }

  getLatestMessage(chat: ChatConversation): ChatApiMessage | null {
    if (chat.latest_message && !this.shouldHideAcknowledgement(chat, chat.latest_message)) {
      return chat.latest_message;
    }

    const messages = this.getDisplayedMessages(chat);

    if (messages.length) {
      return messages[messages.length - 1];
    }

    return null;
  }

  getDisplayedMessages(chat?: ChatConversation | null): ChatApiMessage[] {
    const messages = chat?.messages || [];

    if (!this.hasAdminReply(messages)) {
      return messages;
    }

    return messages.filter(
      (message) => !this.isAcknowledgementMessage(message)
    );
  }

  getDisplayedMessagesCount(chat?: ChatConversation | null): number {
    if (!chat) {
      return 0;
    }

    if (Array.isArray(chat.messages)) {
      return this.getDisplayedMessages(chat).length;
    }

    return chat.messages_count || 0;
  }

  getMessageText(message?: ChatApiMessage | null): string {
    if (!message) {
      return this.t('atw.myChats.noMessagesYet');
    }

    return (
      message.message ||
      this.getMessageKeyLabel(message.message_key) ||
      this.t('atw.myChats.messageWithoutText')
    );
  }

  getSenderLabel(senderType: ChatApiMessage['sender_type']): string {
    return this.t(`atw.myChats.senders.${senderType}`) || senderType;
  }

  formatDate(dateValue?: string | null): string {
    if (!dateValue) {
      return '-';
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return '-';
    }

    return date.toLocaleString(this.getDateLocale(), {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  }

  private getMessageKeyLabel(messageKey?: string | null): string {
    return messageKey ? this.t(messageKey) || messageKey : '';
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.transloco.translate(key, params);
  }

  private getDateLocale(): string {
    return this.transloco.getActiveLang() === 'ar' ? 'ar-EG' : 'en-US';
  }

  private shouldHideAcknowledgement(
    chat: ChatConversation,
    message: ChatApiMessage
  ): boolean {
    return this.hasAdminReply(chat.messages || []) && this.isAcknowledgementMessage(message);
  }

  private hasAdminReply(messages: ChatApiMessage[]): boolean {
    return messages.some((message) => message.sender_type === 'admin');
  }

  private isAcknowledgementMessage(message: ChatApiMessage): boolean {
    const text = (message.message || '').trim();
    const isSystemSender =
      message.sender_type === 'bot' || message.sender_type === 'system';

    return (
      isSystemSender &&
      (message.message_key === 'atw.chat.messages.reply' ||
        text.includes('\u062a\u0645 \u0627\u0633\u062a\u0644\u0627\u0645 \u0631\u0633\u0627\u0644\u062a\u0643') ||
        text.includes('Your message has been received'))
    );
  }

  private queueScrollToBottom(): void {
    this.shouldScrollToBottom = true;
  }

  private scrollMessagesToBottom(): void {
    const container = this.messagesScroller?.nativeElement;

    if (!container) {
      return;
    }

    container.scrollTop = container.scrollHeight;
    this.shouldScrollToBottom = false;
  }
}
